/**
 * Live cut: cutting the program in real time, the way a vision mixer or an
 * NLE's multicam record mode does. These are the rules, kept free of media
 * and React so they can be tested on their own.
 *
 *  - A press cuts the program to an angle on the frame under the playhead. It
 *    adds a cut, retargets the cut already on that frame, or does nothing when
 *    that angle is already live there. An angle with no footage on that frame
 *    is never cut to, and such a press changes nothing.
 *  - A take is one continuous playback. When a press follows the take's
 *    previous press, the cuts strictly between the two frames (other than the
 *    take's own) are replaced, so a second pass leaves no stale cuts. Nothing
 *    before the take's first press or after its last press is touched.
 *  - Every change is a list of steps. Steps apply to the studio's annotations
 *    at once, are saved one request each, and invert for undo.
 *
 * Cuts are tracked by a key rather than their id: a cut added live has a
 * placeholder id until the server answers, and keeps that placeholder as its
 * key afterwards, so queued requests and undo can still find it.
 */

import type { MulticamAnnotation } from '../../graphqlService';
import { getAnnotationCategory } from '../../utils/annotationCategories';
import { pluralize } from '../../utils/format';
import { formatTimecode, secondsToFrames } from '../../utils/timecode';
import {
  CUT_CATEGORY,
  angleName,
  cutLabel,
  findAngle,
  isCut,
  liveAngleKeyAt,
  msFromSeconds,
  sortAnnotations,
  sortedCuts,
} from './multicamStudioModel';

type NamedAngle = { angleKey: string; label?: string | null };

/** Maps an annotation id to the key live cutting tracks it by. */
export type KeyOf = (id: string) => string;

const sameId: KeyOf = (id) => id;

// ---------------------------------------------------------------------------
// Takes and presses
// ---------------------------------------------------------------------------

/** What the current take has done so far. */
export interface LiveTake {
  /** Seconds of the take's previous press; null before its first press. */
  lastPressTime: number | null;
  /** Keys of the cuts this take added or retargeted. A take never replaces these. */
  ownKeys: string[];
}

export const emptyTake = (): LiveTake => ({ lastPressTime: null, ownKeys: [] });

/** Why a press changed nothing (and does not count as the take's press). */
export type LiveBlockReason = 'read-only' | 'unknown-angle' | 'no-footage' | 'end';

export interface LiveBlockedPlan {
  kind: 'blocked';
  reason: LiveBlockReason;
  angleKey: string;
  /** Seconds. */
  time: number;
}

export interface LiveCutPlan {
  /** `already-live` can still replace cuts: the angle stays live from the previous press. */
  kind: 'add' | 'retarget' | 'already-live';
  angleKey: string;
  /** Seconds where the press lands (the cut's time when one is added). */
  time: number;
  frame: number;
  /** Existing cuts strictly between the take's previous press and this one. */
  replaced: MulticamAnnotation[];
  /** The cut on this frame that a retarget changes. */
  existing: MulticamAnnotation | null;
}

export type LivePressPlan = LiveBlockedPlan | LiveCutPlan;

export interface LivePressInput {
  annotations: MulticamAnnotation[];
  angleKey: string;
  /** Seconds on the shared timeline, read from the clock at the press. */
  time: number;
  fps: number;
  masterKey: string;
  /** Length of the shared timeline in seconds; 0 when not known yet. */
  duration: number;
  angleKeys: string[];
  /** Whether an angle has footage at a moment (seconds). */
  hasFootage: (angleKey: string, seconds: number) => boolean;
  take: LiveTake;
  keyOf?: KeyOf;
}

const frameOf = (annotation: MulticamAnnotation, fps: number) =>
  secondsToFrames(annotation.timelineTimeMs / 1000, fps);

/**
 * What a press does. Frames, not milliseconds, decide "the same frame" and
 * "strictly between", so the result does not depend on where in a frame the
 * press landed.
 *
 * The take's replacement is worked out first, and the press is judged on what
 * remains: after "B at 0:05" and old cuts at 0:10 (to C) and 0:20 (to A), a
 * press of A at 0:25 removes both old cuts, then cuts to A because B is what
 * shows there now.
 */
export const planLivePress = ({
  annotations,
  angleKey,
  time,
  fps,
  masterKey,
  duration,
  angleKeys,
  hasFootage,
  take,
  keyOf = sameId,
}: LivePressInput): LivePressPlan => {
  const at = Math.max(0, Number.isFinite(time) ? time : 0);
  if (!angleKeys.includes(angleKey)) return { kind: 'blocked', reason: 'unknown-angle', angleKey, time: at };
  if (duration > 0 && at >= duration) return { kind: 'blocked', reason: 'end', angleKey, time: at };
  if (!hasFootage(angleKey, at)) return { kind: 'blocked', reason: 'no-footage', angleKey, time: at };

  const frame = secondsToFrames(at, fps);
  const cuts = sortedCuts(annotations);

  let replaced: MulticamAnnotation[] = [];
  if (take.lastPressTime !== null) {
    const previous = secondsToFrames(take.lastPressTime, fps);
    // Symmetric, so clock jitter of a frame backwards cannot widen the range.
    const low = Math.min(previous, frame);
    const high = Math.max(previous, frame);
    replaced = cuts.filter((cut) => {
      const cutFrame = frameOf(cut, fps);
      return cutFrame > low && cutFrame < high && !take.ownKeys.includes(keyOf(cut.id));
    });
  }
  const replacedIds = replaced.map((cut) => cut.id);
  const remaining = replacedIds.length
    ? annotations.filter((annotation) => !replacedIds.includes(annotation.id))
    : annotations;

  // The cut that shows from this frame: the last one on it.
  const onFrame = cuts.filter((cut) => frameOf(cut, fps) === frame);
  const existing = onFrame.length ? onFrame[onFrame.length - 1] : null;
  if (existing) {
    const same = existing.targetAngleKey === angleKey;
    return { kind: same ? 'already-live' : 'retarget', angleKey, time: at, frame, replaced, existing: same ? null : existing };
  }
  const live = liveAngleKeyAt(remaining, at, masterKey, fps);
  return { kind: live === angleKey ? 'already-live' : 'add', angleKey, time: at, frame, replaced, existing: null };
};

// ---------------------------------------------------------------------------
// Steps: the changes a live action makes
// ---------------------------------------------------------------------------

export type LiveStep =
  | {
      kind: 'add';
      key: string;
      annotation: MulticamAnnotation;
      /** Set when undo brings back a replaced cut: the key of the cut it replaces. */
      restores?: string;
    }
  | { kind: 'retarget'; key: string; from: string; to: string }
  | { kind: 'delete'; key: string; annotation: MulticamAnnotation };

/** A camera cut as the studio stores it, with no reason text. */
export const newLiveCut = (
  key: string,
  multicamSetId: string,
  time: number,
  angleKey: string,
  now: string = new Date().toISOString()
): MulticamAnnotation => ({
  id: key,
  multicamSetId,
  timelineTimeMs: msFromSeconds(time),
  endTimeMs: null,
  type: 'SWITCH',
  targetAngleKey: angleKey,
  text: '',
  category: CUT_CATEGORY,
  color: getAnnotationCategory(CUT_CATEGORY).color,
  createdAt: now,
  updatedAt: now,
});

/**
 * The steps for a press, in the order they are saved: the cut first, then the
 * cuts it replaces. If saving stops part-way, the program keeps the old cuts
 * rather than losing them without the new one.
 */
export const stepsForPlan = (
  plan: LiveCutPlan,
  newKey: () => string,
  multicamSetId: string,
  keyOf: KeyOf = sameId
): LiveStep[] => {
  const steps: LiveStep[] = [];
  if (plan.kind === 'add') {
    const key = newKey();
    steps.push({ kind: 'add', key, annotation: newLiveCut(key, multicamSetId, plan.time, plan.angleKey) });
  } else if (plan.kind === 'retarget' && plan.existing) {
    steps.push({ kind: 'retarget', key: keyOf(plan.existing.id), from: String(plan.existing.targetAngleKey), to: plan.angleKey });
  }
  plan.replaced.forEach((cut) => steps.push({ kind: 'delete', key: keyOf(cut.id), annotation: cut }));
  return steps;
};

/** The annotations with the steps applied. Steps find their cut by key. */
export const applyLiveSteps = (
  annotations: MulticamAnnotation[],
  steps: LiveStep[],
  keyOf: KeyOf = sameId
): MulticamAnnotation[] => {
  let next = annotations;
  steps.forEach((step) => {
    if (step.kind === 'add') {
      if (!next.some((annotation) => keyOf(annotation.id) === step.key)) next = sortAnnotations([...next, step.annotation]);
    } else if (step.kind === 'retarget') {
      next = next.map((annotation) => (keyOf(annotation.id) === step.key ? { ...annotation, targetAngleKey: step.to } : annotation));
    } else {
      next = next.filter((annotation) => keyOf(annotation.id) !== step.key);
    }
  });
  return next;
};

/**
 * The steps that undo `steps`, last first. Checked against the annotations as
 * they are now, so undo never touches something changed since: a cut that
 * is gone (or is no longer a cut) is not deleted again, and a cut retargeted
 * again since is left as it is. Replaced cuts come back under new keys (the
 * server gives them new ids).
 */
export const invertLiveSteps = (
  steps: LiveStep[],
  annotations: MulticamAnnotation[],
  newKey: () => string,
  keyOf: KeyOf = sameId
): LiveStep[] => {
  const inverse: LiveStep[] = [];
  for (let index = steps.length - 1; index >= 0; index -= 1) {
    const step = steps[index];
    const current = annotations.find((annotation) => keyOf(annotation.id) === step.key);
    if (step.kind === 'add') {
      if (current && isCut(current)) inverse.push({ kind: 'delete', key: step.key, annotation: current });
    } else if (step.kind === 'retarget') {
      if (current && isCut(current) && current.targetAngleKey === step.to) {
        inverse.push({ kind: 'retarget', key: step.key, from: step.to, to: step.from });
      }
    } else if (!current) {
      const key = newKey();
      inverse.push({ kind: 'add', key, annotation: { ...step.annotation, id: key }, restores: step.key });
    }
  }
  return inverse;
};

// ---------------------------------------------------------------------------
// Words for what happened
// ---------------------------------------------------------------------------

const nameOf = (angleKey: string, angles: NamedAngle[]) => {
  const angle = findAngle(angles, angleKey);
  return angle ? angleName(angle) : `Angle ${angleKey}`;
};

/** "Cut to B · Close-up · 00:12:03:14", or why nothing changed. */
export const describeLivePress = (
  plan: LivePressPlan,
  angles: NamedAngle[],
  fps: number,
  readOnlyMessage = 'Cuts can’t be changed in this set.'
): string => {
  const at = formatTimecode(plan.time, fps);
  if (plan.kind === 'blocked') {
    if (plan.reason === 'read-only') return readOnlyMessage;
    if (plan.reason === 'unknown-angle') return `This set has no angle ${plan.angleKey}.`;
    if (plan.reason === 'no-footage') return `${nameOf(plan.angleKey, angles)} has no footage at ${at}.`;
    return 'That’s the end of the set.';
  }
  const replaced = plan.replaced.length ? ` · replaced ${pluralize(plan.replaced.length, 'cut')}` : '';
  if (plan.kind === 'already-live') return `${nameOf(plan.angleKey, angles)} is already live${replaced}`;
  if (plan.kind === 'retarget') return `${cutLabel(plan.angleKey, angles)} · ${at} · changed from ${plan.existing?.targetAngleKey ?? '?'}${replaced}`;
  return `${cutLabel(plan.angleKey, angles)} · ${at}${replaced}`;
};

/** "Undid: Cut to B · Close-up · 00:12:03:14". */
export const describeLiveUndo = (plan: LiveCutPlan, angles: NamedAngle[], fps: number): string =>
  `Undid: ${describeLivePress(plan, angles, fps)}`;
